import {
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  DialogActionTrigger,
  Button,
  VStack,
  Text,
  Box,
  Spinner,
} from '@chakra-ui/react';
import { useState } from 'react';

import { JsonEditor } from '#src/components/json-editor';
import { toaster } from '#src/components/ui/toaster';
import { useExecuteProcessorMutation } from '#src/components/use-execute-processor-mutation';

interface ExecuteProcessorModalProps {
  open: boolean;
  processorId: string;
  processorName?: string;
  onClose: () => void;
}

export function ExecuteProcessorModal({
  open,
  processorId,
  processorName,
  onClose,
}: ExecuteProcessorModalProps) {
  const [argsJson, setArgsJson] = useState('{}');
  const [outputJson, setOutputJson] = useState<string | null>(null);

  const [executeProcessor, { loading }] =
    useExecuteProcessorMutation(setOutputJson);

  const handleSubmit = () => {
    setOutputJson(null);
    let variables: Record<string, unknown>;
    try {
      variables = JSON.parse(argsJson);
      if (typeof variables !== 'object' || variables === null) {
        variables = {};
      }
    } catch {
      toaster.create({
        title: 'Invalid JSON',
        description: 'Please enter valid JSON for the arguments',
        type: 'error',
        duration: 5000,
        closable: true,
      });
      return;
    }

    executeProcessor({
      variables: {
        input: { processorId, variables },
      },
    });
  };

  const handleClose = () => {
    setArgsJson('{}');
    setOutputJson(null);
    onClose();
  };

  const title = processorName
    ? `Execute: ${processorName}`
    : 'Execute Processor';

  return (
    <DialogRoot
      open={open}
      onOpenChange={(e) => {
        if (!e.open) handleClose();
      }}
      size="xl"
    >
      <DialogContent maxW="90vw" maxH="90vh">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <DialogBody
          overflowY="auto"
          display="flex"
          flexDirection="column"
          gap={4}
        >
          <VStack gap={2} align="stretch">
            <Text fontSize="sm" color="fg.muted">
              Arguments (JSON object passed as ctx.variables)
            </Text>
            <JsonEditor
              value={argsJson}
              onChange={setArgsJson}
              placeholder="{}"
              height="200px"
              disabled={loading}
            />
          </VStack>

          {loading && (
            <Box display="flex" alignItems="center" gap={2} py={2}>
              <Spinner size="sm" />
              <Text fontSize="sm" color="fg.muted">
                Running processor...
              </Text>
            </Box>
          )}

          {outputJson !== null && !loading && (
            <VStack gap={2} align="stretch">
              <Text fontSize="sm" color="fg.muted">
                Output
              </Text>
              <Box
                as="pre"
                p={4}
                bg="bg.subtle"
                borderRadius="md"
                borderWidth="1px"
                borderColor="border"
                fontSize="sm"
                fontFamily="mono"
                whiteSpace="pre-wrap"
                wordBreak="break-word"
                overflowX="auto"
                maxH="300px"
                overflowY="auto"
              >
                {outputJson}
              </Box>
            </VStack>
          )}
        </DialogBody>
        <DialogFooter>
          <DialogActionTrigger asChild>
            <Button variant="outline" disabled={loading}>
              Close
            </Button>
          </DialogActionTrigger>
          <Button
            colorScheme="blue"
            onClick={handleSubmit}
            loading={loading}
            disabled={loading}
          >
            Submit
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
}
